import { useCallback, useRef } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"
import { DOMParser as ProseMirrorDOMParser, Schema } from "@tiptap/pm/model"
import { EditorState } from "@tiptap/pm/state"
import { prosemirrorToYXmlFragment, yUndoPluginKey } from "@tiptap/y-tiptap"

import { CollaborationService } from "services/collaborationService"
import { ExclusiveSave } from "./useQuestionnaireSave"
import { notify } from "shared/ui/Notifications/notify"
import {
  EditorRef,
  LoadedQuestionnaire,
  QuestionnaireSaveOptions,
  Variant,
} from "types/questionnaire.types"

export interface UseRestoreVersionProps {
  /** The TipTap editor instance (its schema reads the restored HTML) */
  editor: TiptapEditor | null
  /** The editor's ref, used to put the restored master in the editor */
  editorRef: React.RefObject<EditorRef>
  /** Whether collaboration is enabled */
  collaborationEnabled: boolean
  /** Whether the collaboration WebSocket is connected */
  isConnected: boolean
  /** The collaboration service instance */
  collaborationService: CollaborationService | null
  /** Current selected variant name ("" on Master) */
  currentVariant: string
  /** The variants before the restore */
  variants: Variant[]
  /** Loads a full questionnaire text into the editor state (the container's load path) */
  loadQuestionnaireText: (text: string) => LoadedQuestionnaire
  /** Saves a full questionnaire text as it is (the container's save) */
  saveQuestionnaire: (
    updatedVariantsParam?: Variant[],
    collaborativeContent?: string,
    options?: QuestionnaireSaveOptions,
  ) => Promise<void>
  /** Runs a save after the editor's saves in flight (and a checkpoint of edits not yet in a version), holding them back meanwhile */
  runExclusiveSave: (save: ExclusiveSave) => Promise<void>
}

interface UseRestoreVersionReturn {
  /** Restores a version's text; resolves to false when nothing was restored */
  restoreVersion: (text: string) => Promise<boolean>
}

/**
 * Clears the editor's undo history, so undo can't step back into the old master
 * under the restored variants. With collaboration the history is the Yjs undo
 * manager; without it, the history plugin's state, which only a fresh editor
 * state on the same document clears.
 */
const clearUndoHistory = (editor: TiptapEditor): void => {
  const undoManager = yUndoPluginKey.getState(editor.state)?.undoManager
  if (undoManager) {
    undoManager.clear()
    return
  }
  const { doc, plugins, selection } = editor.state
  editor.view.updateState(EditorState.create({ doc, plugins, selection }))
}

/**
 * Puts a variant's restored text (editor HTML) into its Yjs doc, as a diff against
 * what the doc holds, like the editor's setHTML. A doc that hasn't synced yet is
 * written once it has: written before, the server's copy would be merged on top.
 */
const writeVariantDoc = (
  service: CollaborationService,
  name: string,
  html: string,
  schema: Schema
): void => {
  const doc = service.getVariantDoc(name) ?? service.initializeVariant(name)
  const provider = service.getProvider(name)

  const writeRestoredText = (): void => {
    const element = document.createElement("div")
    element.innerHTML = html
    prosemirrorToYXmlFragment(
      ProseMirrorDOMParser.fromSchema(schema).parse(element),
      doc.getXmlFragment("default")
    )
  }

  if (!provider || provider.synced) {
    writeRestoredText()
    return
  }
  const handleSync = (isSynced: boolean): void => {
    if (!isSynced) return
    provider.off("sync", handleSync)
    writeRestoredText()
  }
  provider.on("sync", handleSync)
}

/**
 * useRestoreVersion restores a whole questionnaire version, master and variants.
 * The text goes through the editor's load path (variants split out, bold and italic
 * kept) and the master goes into the editor. With collaboration, a save reads each
 * variant from its Yjs doc, so each restored variant's text goes into its doc too, and
 * the variants the version doesn't have lose their metadata, so other sessions drop
 * them as well. The version is then saved at once, exactly as recorded, after any
 * save already running and with the editor's saves held back, so no save can mix the
 * old and the restored questionnaire. Edits not yet in a version are saved as a
 * version first, so the text the restore replaces can be restored too. Like any save,
 * a restore is refused while collaboration is disconnected. Restoring is offered on
 * Master only.
 */
export const useRestoreVersion = ({
  editor,
  editorRef,
  collaborationEnabled,
  isConnected,
  collaborationService,
  currentVariant,
  variants,
  loadQuestionnaireText,
  saveQuestionnaire,
  runExclusiveSave,
}: UseRestoreVersionProps): UseRestoreVersionReturn => {
  // The editor, variant and variant list as they are when the restore is applied: the
  // user can switch variant while the version is fetched, and a save that finishes
  // first can change the variant list
  const latestRef = useRef({ editor, currentVariant, variants, isConnected })
  latestRef.current = { editor, currentVariant, variants, isConnected }

  const restoreVersion = useCallback(
    async (text: string): Promise<boolean> => {
      // A save while disconnected could overwrite other users' changes: saves wait for the
      // connection, and so does a restore
      if (collaborationEnabled && !latestRef.current.isConnected) {
        notify.error(
          "Couldn't restore this version",
          "Not connected to the collaboration server. Try again once it reconnects."
        )
        return false
      }
      let restored = false
      await runExclusiveSave(async (keepCurrentText) => {
        const latest = latestRef.current
        // Restoring puts the master in the editor: do nothing on a variant
        if (latest.currentVariant !== "") return
        const activeEditor = latest.editor
        if (!activeEditor || activeEditor.isDestroyed) return
        // The text the restore replaces becomes a version first, so it can be restored too
        await keepCurrentText()
        if (latestRef.current.currentVariant !== "" || activeEditor.isDestroyed) return

        const loaded = loadQuestionnaireText(text)
        editorRef.current?.setHTML(loaded.masterHTML)
        clearUndoHistory(activeEditor)

        if (collaborationEnabled && collaborationService) {
          loaded.variants.forEach(({ name, languages, html }) => {
            collaborationService.updateVariantMetadata(name, languages)
            writeVariantDoc(collaborationService, name, html, activeEditor.schema)
          })
          const restoredNames = new Set(loaded.variants.map((variant) => variant.name))
          latest.variants
            .filter((variant) => !restoredNames.has(variant.name))
            .forEach((variant) => collaborationService.removeVariantMetadata(variant.name))
        }
        restored = true

        // A restore is a new version: this save is a checkpoint. It saves the text
        // as recorded (the container's save also sets the variant list and metadata).
        await saveQuestionnaire(undefined, text, { checkpoint: true })
        // As after any save: the Yjs docs now match what was saved
        collaborationService?.recordSaveTimestamp("master")
        loaded.variants.forEach(({ name }) => collaborationService?.recordSaveTimestamp(name))
      })
      return restored
    },
    [
      editorRef,
      collaborationEnabled,
      collaborationService,
      loadQuestionnaireText,
      saveQuestionnaire,
      runExclusiveSave,
    ]
  )

  return { restoreVersion }
}
